import { WithDisposable } from '@blocksuite/affine/global/lit';
import { scrollbarStyle } from '@blocksuite/affine/shared/styles';
import { ShadowlessElement } from '@blocksuite/affine/std';
import type { PropertyValues } from 'lit';
import { css, html } from 'lit';
import { property, query } from 'lit/decorators.js';
import throttle from 'lodash-es/throttle';

import type {
  AffineAIPanelState,
  AffineAIPanelWidgetConfig,
} from '../widgets/ai-panel/type';
import type { TextRendererOptions } from './text-renderer';

export class AIScrollableTextRenderer extends WithDisposable(
  ShadowlessElement
) {
  static override styles = css`
    .ai-scrollable-text-renderer {
      overflow-y: auto !important;
      overscroll-behavior: contain;
      pointer-events: auto !important;
      touch-action: pan-y !important;
      user-select: text;
    }

    ${scrollbarStyle('.ai-scrollable-text-renderer')};
  `;

  private _lastScrollHeight = 0;
  private _userScrolledUp = false;

  private readonly _scrollToEnd = () => {
    requestAnimationFrame(() => {
      if (!this._scrollableTextRenderer || this._userScrolledUp) {
        return;
      }
      const scrollHeight = this._scrollableTextRenderer.scrollHeight || 0;

      if (scrollHeight > this._lastScrollHeight) {
        this._lastScrollHeight = scrollHeight;
        this._scrollableTextRenderer?.scrollTo({
          top: scrollHeight,
        });
      }
    });
  };

  private readonly _throttledScrollToEnd = throttle(this._scrollToEnd, 300);

  private readonly _onWheel = (e: WheelEvent) => {
    e.stopPropagation();
    if (this._scrollableTextRenderer) {
      if (e.deltaY < 0) {
        this._userScrolledUp = true;
      } else if (
        this._scrollableTextRenderer.scrollTop +
          this._scrollableTextRenderer.clientHeight >=
        this._scrollableTextRenderer.scrollHeight - 30
      ) {
        this._userScrolledUp = false;
      }
    }
  };

  private readonly _onScroll = () => {
    if (!this._scrollableTextRenderer) return;
    const { scrollTop, clientHeight, scrollHeight } = this._scrollableTextRenderer;
    if (scrollTop + clientHeight < scrollHeight - 40) {
      this._userScrolledUp = true;
    } else {
      this._userScrolledUp = false;
    }
  };

  protected override updated(_changedProperties: PropertyValues) {
    if (_changedProperties.has('state') && this.state === 'finished') {
      // Allow user freedom to scroll anywhere once finished
      this._userScrolledUp = false;
    }
    if (
      this.autoScroll &&
      _changedProperties.has('answer') &&
      (this.state === 'generating' || this.state === 'finished')
    ) {
      this._throttledScrollToEnd();
    }
  }

  override render() {
    const { answer, state, textRendererOptions } = this;

    return html` <style>
        .ai-scrollable-text-renderer {
          max-height: ${this.maxHeight}px;
        }
      </style>
      <div
        class="ai-scrollable-text-renderer"
        @wheel=${this._onWheel}
        @scroll=${this._onScroll}
      >
        <text-renderer
          .answer=${answer}
          .state=${state}
          .options=${textRendererOptions}
        ></text-renderer>
      </div>`;
  }

  @property({ attribute: false })
  accessor answer!: string;

  @property({ attribute: false })
  accessor state: AffineAIPanelState | undefined;

  @property({ attribute: false })
  accessor textRendererOptions!: TextRendererOptions;

  @property({ attribute: false })
  accessor maxHeight = 320;

  @property({ attribute: false })
  accessor autoScroll = true;

  @query('.ai-scrollable-text-renderer')
  accessor _scrollableTextRenderer: HTMLDivElement | null = null;
}

export const createAIScrollableTextRenderer: (
  textRendererOptions: TextRendererOptions,
  maxHeight: number,
  autoScroll: boolean
) => AffineAIPanelWidgetConfig['answerRenderer'] = (
  textRendererOptions,
  maxHeight,
  autoScroll
) => {
  return (answer: any, state: AffineAIPanelState | undefined) => {
    const textContent =
      typeof answer === 'string'
        ? answer
        : typeof answer?.content === 'string'
          ? answer.content
          : '';
    return html`<ai-scrollable-text-renderer
      .answer=${textContent}
      .state=${state}
      .textRendererOptions=${textRendererOptions}
      .maxHeight=${maxHeight}
      .autoScroll=${autoScroll}
    ></ai-scrollable-text-renderer>`;
  };
};

declare global {
  interface HTMLElementTagNameMap {
    'ai-scrollable-text-renderer': AIScrollableTextRenderer;
  }
}
